import { auth } from '../../constants/firebaseModular';

/**
 * SERVER TIME for the MFA gate — CU-86ajwpnek §9 (N3), ported from
 * `mideo-health-app`. Only the SDK import differed; the reasoning is identical.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 * `decideMfaGate` will honor an admin-granted Tier-2 `mfa.exception` only when
 * it can compare `expiresAt` against SERVER time. The device clock is
 * spoofable: anyone holding the device can wind it back and extend an expired
 * PHI bypass indefinitely. So the gate takes `serverTimeMs` as an input and
 * refuses the bypass when it is `null`.
 *
 * ⚠️ This is exactly what D3 measured on device (2026-08-05): an exception
 * holder who passed the gate ONLINE was correctly GATED offline, because the
 * bypass cannot be dated without a trusted clock.
 *
 * ── WHY A TOKEN REFRESH IS A LEGITIMATE CLOCK ─────────────────────────────
 * Two independent properties, both needed:
 *
 *  1. A successful FORCED refresh proves we reached Google just now. It cannot
 *     be satisfied from cache — that is what `forceRefresh: true` means — so it
 *     doubles as the connectivity proof N3 requires.
 *  2. The returned token's `iat` ("issued at") is stamped by GOOGLE when it
 *     mints the token, not by this device. Nothing local can forge it.
 *
 * Together: if we hold a freshly-minted token, its `iat` is server time to
 * within the round-trip — far inside the tolerance for an expiry measured in
 * days.
 *
 * ⚠️ We deliberately do NOT fall back to `Date.now()` anywhere in this file.
 * That value is precisely what N3 exists to distrust, and a silent fallback
 * would reintroduce the bypass while looking like it was handled.
 */

/**
 * Best-effort server time, in epoch milliseconds.
 *
 * @returns epoch ms from the freshly-minted ID token's `iat`, or `null` when it
 *          could not be established (offline, no session, malformed claim).
 *
 * `null` is a normal, expected outcome — not an error. It means "no trusted
 * clock", and `decideMfaGate` correctly withholds the exception bypass for it.
 * A genuine `aal2` session is unaffected: that claim is cryptographically
 * issued and not clock-dependent, so it still passes offline (D1).
 *
 * Never throws.
 */
export async function getServerTimeMs(): Promise<number | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    // forceRefresh: true is load-bearing — a cached token proves nothing about
    // connectivity and its `iat` could be hours old.
    const result = await user.getIdTokenResult(true);

    // `iat` is in SECONDS (JWT convention). Multiplying by 1000 here — rather
    // than at the call site — keeps the seconds/millis mistake in one place.
    const iat = (result.claims as { iat?: unknown })?.iat;
    if (typeof iat === 'number' && Number.isFinite(iat)) {
      return iat * 1000;
    }

    // The SDK also surfaces issuance time as an ISO string. Use it only as a
    // secondary read of the SAME server-minted value — never as a device-clock
    // substitute.
    const issuedAt = result.issuedAtTime;
    if (typeof issuedAt === 'string') {
      const parsed = Date.parse(issuedAt);
      if (Number.isFinite(parsed)) return parsed;
    }

    // A token we cannot date is not a clock. Fail to `null` (bypass withheld).
    return null;
  } catch {
    // Offline, revoked session, or any SDK fault. All mean the same thing here:
    // no trusted clock. NOT an error worth reporting — being offline is normal.
    return null;
  }
}
